import { ChevronRight } from '@mui/icons-material';
import { type PlayerItemResponse, RankingType } from '@on3/api';
import { On3IconAlert } from '@on3/icons';
import { Avatar } from '@on3/ui-lib/components/Avatar';
import { Link } from '@on3/ui-lib/components/Link';
import { StarRating } from '@on3/ui-lib/components/Player/StarRating';
import { StarRanking } from '@on3/ui-lib/components/Player/StarRating/Ranking';
import { useSite } from '@on3/ui-lib/index';
import { classYearClaimCutoff } from '@on3/ui-lib/utils/constants';
import { formatCurrency } from '@on3/ui-lib/utils/formatters';
import { useDynamicRoute } from '@on3/ui-lib/utils/hooks/useDynamicRoute';
import { slugify } from '@on3/ui-lib/utils/slugify';
import { createUrl } from '@on3/ui-lib/utils/url';
import { VerifiedBadge } from '@public/components/Elite/VerifiedBadge/VerifiedBadge';
import { EarlyEnrolleeIcon } from '@public/components/Icons/EarlyEnrolleeIcon';
import { EarlySigneeIcon } from '@public/components/Icons/EarlySigneeIcon';
import { SportIcon } from '@public/components/Icons/SportIcon';
import { StatusContainer } from '@public/components/RecruitmentStatus/StatusContainer/StatusContainer';
import clsx from 'clsx';
import { format } from 'date-fns';

import styles from './CommitListItem.module.scss';

export const CommitListItem = ({ data }: { data: PlayerItemResponse }) => {
  const {
    player,
    status,
    rating,
    nilValue,
    rpm,
    transferRating,
    dateDecommitted,
  } = data || {};

  const {
    earlyEnrollee,
    earlySignee,
    fullName,
    defaultAsset,
    slug,
    highSchool,
    hometown,
    position,
    athleteVerified,
    height,
    weight,
    classYear,
    state,
    tier,
    defaultSport: playerSport,
  } = player || {};
  const { transferredAsset } = status || {};
  const { formatHref, routeParams } = useDynamicRoute();
  const { currentSite, sportsData } = useSite();
  const { sport } = routeParams;
  const showIcons = earlyEnrollee || earlySignee;
  const showNil = !!nilValue?.totalValue && nilValue?.totalValue >= 5000;
  const defaultSportKey = playerSport?.key;
  const isAllSports = !sport;
  const isTransfer = status?.transfer;

  const isShortTermSignee = status?.shortTermSignee;
  const isShortTermTeam =
    isShortTermSignee && transferredAsset?.key === currentSite?.organizationKey;

  const currentSport = sportsData?.find((sportData) => {
    return slugify(sportData.name) === sport;
  });
  const isIndustrySport = currentSport?.isIndustryRankable;

  const industryComparisonLink =
    slug &&
    formatHref({
      path: '/rivals/[id]/industry-comparison/',
      params: { id: slug },
      options: { resetQuery: true },
    });

  return (
    <div
      className={clsx(styles.playerWrapper, {
        [styles.shortTerm]: isShortTermTeam,
      })}
    >
      <figure className={styles.avatar}>
        <Avatar
          altText={`${fullName} Avatar`}
          domain={defaultAsset?.domain}
          fit="cover"
          height={100}
          quality={95}
          source={defaultAsset?.source}
          type={defaultAsset?.type}
          width-={100}
        />
      </figure>

      <div className={styles.details}>
        <div className={styles.nameContainer}>
          {isShortTermTeam && (
            <div className={styles.shortTermSignee}>
              <On3IconAlert height={15} width={15} />
              <span>Short-term signee</span>
            </div>
          )}
          <div className={styles.nameAndSport}>
            <Link href={`/rivals/${slug}/`}>{fullName}</Link>

            {isAllSports && defaultSportKey && (
              <SportIcon
                className={styles.sportIcon}
                sportName={defaultSportKey}
              />
            )}
          </div>
        </div>
        <div className={styles.homeContainer}>
          <dl>
            <dt>High School:</dt>
            <dd>
              <Link href={createUrl(['high-school', highSchool?.urlSlug])}>
                {highSchool?.name}
              </Link>
            </dd>

            <dt>Hometown:</dt>
            <dd>({hometown?.abbr})</dd>
          </dl>
          {typeof dateDecommitted === 'string' && (
            <dl className={styles.decommitted}>
              <dt>Decommitted:</dt>
              <dd>
                <b>{format(new Date(dateDecommitted), 'M/dd/yy')}</b>
              </dd>
            </dl>
          )}
        </div>
        <div className={styles.vitalsContainer}>
          {isTransfer && position?.abbr ? (
            <dl>
              <dt>Position Abbreviation</dt>
              <dd>{position?.abbr}</dd>
            </dl>
          ) : (
            <dl>
              <dt>Class year</dt>
              <dd>{classYear} H.S.</dd>
            </dl>
          )}

          {!!height && (
            <dl>
              <dt>Height</dt>
              <dd>{height}</dd>
            </dl>
          )}
          {!!weight && (
            <dl>
              <dt>Weight</dt>
              <dd>{weight}</dd>
            </dl>
          )}
        </div>
        {showIcons && (
          <div className={styles.earlyIconsContainer}>
            {earlyEnrollee && (
              <EarlyEnrolleeIcon className={styles.earlyIcon} />
            )}
            {earlySignee && <EarlySigneeIcon className={styles.earlyIcon} />}
          </div>
        )}
      </div>

      <div className={styles.stars}>
        {isTransfer && (
          <>
            <StarRating
              fiveStarPlus={transferRating?.fiveStarPlus}
              href={industryComparisonLink}
              rating={transferRating?.rating}
              sport={transferRating?.sport?.name}
              stars={transferRating?.stars}
              type={RankingType.TransferPortal}
              year={transferRating?.year}
            />

            <StarRating
              className={styles.secondaryStars}
              fiveStarPlus={transferRating?.fiveStarPlus}
              href={industryComparisonLink}
              rating={transferRating?.consensusRating}
              sport={transferRating?.sport?.name}
              stars={transferRating?.stars}
              year={transferRating?.year}
            />
          </>
        )}
        {!isTransfer && (
          <>
            {isIndustrySport ? (
              <StarRating
                fiveStarPlus={rating?.fiveStarPlus}
                href={industryComparisonLink}
                rating={rating?.consensusRating}
                sport={rating?.sport?.name}
                stars={rating?.consensusStars}
                year={rating?.year}
              />
            ) : (
              <StarRating
                fiveStarPlus={rating?.fiveStarPlus}
                href={industryComparisonLink}
                rating={rating?.rating}
                sport={rating?.sport?.name}
                stars={rating?.stars}
                year={rating?.year}
              />
            )}
          </>
        )}
        <StarRanking
          isConsensus={isIndustrySport}
          nationalRank={
            isIndustrySport
              ? rating?.consensusNationalRank
              : rating?.nationalRank
          }
          positionAbbr={rating?.positionAbbr}
          positionRank={
            isIndustrySport
              ? rating?.consensusPositionRank
              : rating?.positionRank
          }
          sport={rating?.sport?.name}
          stateAbbr={rating?.stateAbbr}
          stateRank={
            isIndustrySport ? rating?.consensusStateRank : rating?.stateRank
          }
          year={rating?.year}
        />
      </div>

      <dl className={styles.nilContainer}>
        <dt>NIL Value:</dt>
        <dd>
          {showNil && nilValue.totalValue
            ? formatCurrency(nilValue.totalValue)
            : '-'}
        </dd>
      </dl>
      <div className={styles.verified}>
        {athleteVerified ? (
          <VerifiedBadge tier={tier} />
        ) : (classYear || 0) > classYearClaimCutoff ? (
          <Link
            forceReload
            href={createUrl(['rivals', slug, 'claim-profile'])}
            useSiteColor
          >
            Claim Profile <ChevronRight />
          </Link>
        ) : null}
      </div>
      {!isTransfer && (
        <dl className={styles.positionContainer}>
          <dt>Position Abbreviation</dt>
          <dd>{position?.abbr}</dd>
        </dl>
      )}

      <div
        className={styles.statusWrapper}
        data-type={isTransfer ? 'transfer' : 'normal'}
      >
        <StatusContainer
          player={player}
          rpm={rpm}
          sport={currentSport}
          status={status}
        />
      </div>
    </div>
  );
};
